import { formatMoney, round2 } from "../format-utils"

export const BREAKDOWN_VIEW_TRADE = "trade"
export const BREAKDOWN_VIEW_SCHEDULE = "schedule"
export const BREAKDOWN_VIEW_ASSET = "asset"
export const ALLOCATED_FIXED_CONTRACT_TOOLTIP =
    "Representative allocation from a fixed annual contract: annual contract amount divided by covered tasks, then multiplied by this row's covered tasks. Pricing is still counted once in Fixed Annual Contracts."

const DETAIL_TYPE_TRADE = "trade"
const DETAIL_TYPE_SCHEDULE = "schedule"
const DETAIL_TYPE_ASSET = "asset"
const MODE_FIXED_ANNUAL = "fixed-annual"
const COST_BASIS_ALLOCATED_FIXED_ANNUAL = "Allocated fixed annual contract"

export function getCostBreakdownViewConfig(view) {
    switch (view) {
        case BREAKDOWN_VIEW_SCHEDULE:
            return {
                primaryColumnLabel: "Schedules",
                totalLabel: "Schedule Costs",
                emptyMessage: "No schedule cost data to display.",
                showTaskCount: true,
                useMaintenanceSubtotal: false,
            }
        case BREAKDOWN_VIEW_ASSET:
            return {
                primaryColumnLabel: "Assets",
                totalLabel: "Asset Costs",
                emptyMessage: "No asset cost data to display.",
                showTaskCount: true,
                useMaintenanceSubtotal: false,
            }
        case BREAKDOWN_VIEW_TRADE:
        default:
            return {
                primaryColumnLabel: "Trades",
                totalLabel: "Trade Costs",
                emptyMessage: "No trade cost data to display.",
                showTaskCount: false,
                useMaintenanceSubtotal: true,
            }
    }
}

export function buildCostBreakdownRows(costResult, view) {
    switch (view) {
        case BREAKDOWN_VIEW_SCHEDULE:
            return buildScheduleBreakdownRows(costResult)
        case BREAKDOWN_VIEW_ASSET:
            return buildAssetBreakdownRows(costResult)
        case BREAKDOWN_VIEW_TRADE:
        default:
            return buildTradeBreakdownRows(costResult)
    }
}

export function filterCostBreakdownRows(rows, view, search) {
    if (!isSearchableBreakdownView(view)) return rows

    const terms = getSearchTerms(search)
    if (!terms.length) return rows

    switch (view) {
        case BREAKDOWN_VIEW_SCHEDULE:
            return rows.filter((row) => scheduleRowMatchesSearch(row, terms))
        case BREAKDOWN_VIEW_ASSET:
            return rows.filter((row) => assetRowMatchesSearch(row, terms))
        default:
            return rows
    }
}

export function buildTradeBreakdownRows(costResult) {
    return [...(costResult?.tradeBreakdown || [])].sort(sortByCostDescending).map(toTradeRow)
}

export function buildScheduleBreakdownRows(costResult) {
    return getAllocatedScheduleBreakdown(costResult).map(toScheduleRow)
}

export function buildAssetBreakdownRows(costResult) {
    return getAllocatedAssetBreakdown(costResult).map(toAssetRow).sort(sortByCostDescending)
}

// Server-paginated variants: items are one page of stored breakdown entries already in
// display order. Only the per-row fixed-contract allocation is applied — never sort or
// filter here; the server owns ordering, search and totals for database-backed results.
export function buildServerScheduleRows(items, costResult) {
    const fixedContractsByTradeId = getFixedContractsByTradeId(costResult)
    return (items || []).map(toRow)

    function toRow(item) {
        return toScheduleRow(applyFixedContractAllocationToSchedule(item, fixedContractsByTradeId))
    }
}

export function buildServerAssetRows(items, costResult) {
    const fixedContractsByTradeId = getFixedContractsByTradeId(costResult)
    return (items || []).map(toRow)

    function toRow(item) {
        return toAssetRow(applyFixedContractAllocationToAsset(item, fixedContractsByTradeId))
    }
}

export function buildCostBreakdownDetailRows(row) {
    switch (row?.detailType) {
        case DETAIL_TYPE_TRADE:
            return buildTradeSkillRows(row.detailSource)
        case DETAIL_TYPE_SCHEDULE:
            return buildScheduleTradeRows(row.detailSource)
        case DETAIL_TYPE_ASSET:
            return buildAssetDetailRows(row.detailSource)
        default:
            return row?.detailRows || []
    }
}

export function getBreakdownTotals(rows) {
    return (rows || []).reduce(
        (totals, row) => {
            totals.taskCount += row.taskCount || 0
            totals.actualHours += row.actualHours ?? row.totalHours ?? 0
            totals.totalHours += row.totalHours || 0
            totals.totalCost += row.totalCost || 0
            return totals
        },
        { taskCount: 0, actualHours: 0, totalHours: 0, totalCost: 0 }
    )
}

export function hasBillingAdjustment(rows) {
    return (rows || []).some(hasRowBillingAdjustment)
}

export function buildTradeExportRows({ costResult, currencySymbol }) {
    const trades = buildTradeBreakdownRows(costResult)
    if (!trades.length) return []

    const hasAdjustment = hasBillingAdjustment(trades)
    const rows = []
    for (const trade of trades) {
        rows.push(toTradeExportRow(trade, { hasAdjustment, currencySymbol }))
    }

    const totals = getBreakdownTotals(trades)
    const totalRow = { "Trade Name": "Total" }
    if (hasAdjustment) {
        totalRow["Actual Hours"] = round2(totals.actualHours)
        totalRow["Billed Hours"] = round2(totals.totalHours)
    } else {
        totalRow["Total Hours"] = round2(totals.totalHours)
    }
    totalRow["Hourly Rate"] = ""
    totalRow["Total Cost"] = `${currencySymbol}${formatMoney(totals.totalCost)}`
    totalRow["%"] = "100%"
    rows.push(totalRow)

    return rows
}

export function buildScheduleExportRows({ costResult, currencySymbol }) {
    const schedules = getAllocatedScheduleBreakdown(costResult)
    if (!schedules.length) return []

    const rows = []
    for (const schedule of schedules) {
        const trades = [...(schedule.trades || [])].sort(sortByCostDescending)
        if (!trades.length) {
            rows.push(buildScheduleExportRow({ schedule, currencySymbol }))
            continue
        }

        for (const trade of trades) {
            rows.push(buildScheduleExportRow({ schedule, trade, currencySymbol }))
        }
        rows.push(buildScheduleTotalExportRow({ schedule, currencySymbol }))
    }
    return rows
}

export function buildAssetExportRows({ costResult, currencySymbol }) {
    const assets = getAllocatedAssetBreakdown(costResult)
    if (!assets.length) return []

    const rows = []
    for (const asset of assets) {
        const details = buildAssetDetailRows(asset)
        if (!details.length) {
            rows.push(buildAssetExportRow({ asset, currencySymbol }))
            continue
        }

        for (const detail of details) {
            rows.push(buildAssetExportRow({ asset, detail, currencySymbol }))
        }
        rows.push(buildAssetTotalExportRow({ asset, currencySymbol }))
    }
    return rows
}

function toTradeRow(trade) {
    const isFixed = trade.mode === MODE_FIXED_ANNUAL
    return {
        id: `trade:${trade.tradeId || trade.tradeName}`,
        label: trade.tradeName || "Unknown",
        taskCount: trade.taskCount || 0,
        actualHours: trade.actualHours ?? trade.totalHours ?? 0,
        totalHours: trade.totalHours || 0,
        hourlyRate: trade.hourlyRate,
        totalCost: trade.totalCost || 0,
        percentage: trade.percentage || 0,
        mode: trade.mode || "hourly",
        hasDetailRows: hasTradeSkillRows(trade, isFixed),
        detailType: DETAIL_TYPE_TRADE,
        detailSource: trade,
    }
}

function buildTradeSkillRows(trade) {
    return [...(trade?.skills || [])].sort(sortByCostDescending).map(toSkillRow)
}

function hasTradeSkillRows(trade, isFixed) {
    return !isFixed && (trade.skills || []).length > 1
}

function toSkillRow(skill) {
    return {
        id: `skill:${skill.tradeId || ""}:${skill.skillId || skill.skillName}`,
        label: skill.skillName || "Unknown skill",
        taskCount: skill.taskCount || 0,
        actualHours: null,
        totalHours: skill.totalHours || 0,
        hourlyRate: skill.hourlyRate,
        totalCost: skill.totalCost || 0,
        isOverride: !!skill.isOverride,
        secondaryLabel: getSkillSecondaryLabel(skill),
    }
}

function toScheduleRow(schedule) {
    return {
        id: `schedule:${schedule.facilityId || ""}:${schedule.scheduleId || schedule.scheduleCode || schedule.scheduleTitle}`,
        label: getScheduleLabel(schedule),
        secondaryLabel: schedule.facilityName || "",
        taskCount: schedule.costedTaskCount ?? schedule.taskCount ?? 0,
        actualHours: schedule.actualHours ?? schedule.totalHours ?? 0,
        totalHours: schedule.totalHours || 0,
        totalCost: schedule.totalCost || 0,
        allocatedFixedCost: schedule.allocatedFixedCost || 0,
        hasDetailRows: hasScheduleDetailRows(schedule),
        detailType: DETAIL_TYPE_SCHEDULE,
        detailSource: schedule,
    }
}

function buildScheduleTradeRows(schedule) {
    return [...(schedule?.trades || [])].sort(sortByCostDescending).map(toScheduleTradeRow)
}

function hasScheduleDetailRows(schedule) {
    return (schedule.trades || []).length > 0
}

function toScheduleTradeRow(trade) {
    return {
        id: `schedule-trade:${trade.tradeId || trade.tradeName}`,
        label: trade.tradeName || "Unknown trade",
        taskCount: trade.taskCount || 0,
        actualHours: trade.actualHours ?? trade.totalHours ?? 0,
        totalHours: trade.totalHours || 0,
        hourlyRate: trade.hourlyRate,
        totalCost: trade.totalCost || 0,
        allocatedFixedCost: trade.allocatedFixedCost || 0,
        mode: trade.mode || "hourly",
    }
}

function toAssetRow(asset) {
    const label = getAssetPrimaryLabel(asset)
    return {
        id: `asset:${asset.assetId || asset.assetTag || asset.assetLabel}`,
        label,
        secondaryLabel: getAssetSecondaryLabel(asset, label),
        taskCount: asset.taskCount || 0,
        actualHours: asset.actualHours ?? asset.totalHours ?? 0,
        totalHours: asset.totalHours || 0,
        totalCost: asset.totalCost || 0,
        allocatedFixedCost: asset.allocatedFixedCost || 0,
        percentage: asset.percentage || 0,
        hasDetailRows: hasAssetDetailRows(asset),
        detailType: DETAIL_TYPE_ASSET,
        detailSource: asset,
    }
}

function hasAssetDetailRows(asset) {
    if ((asset.schedules || []).length) return true
    return (asset.trades || []).length > 0
}

function buildAssetDetailRows(asset) {
    const rows = []
    for (const schedule of [...(asset.schedules || [])].sort(sortScheduleBreakdown)) {
        const trades = [...(schedule.trades || [])].sort(sortByCostDescending)
        if (!trades.length) {
            rows.push(toAssetScheduleRow(schedule))
            continue
        }
        for (const trade of trades) {
            rows.push(toAssetScheduleTradeRow(schedule, trade))
        }
    }
    if (rows.length) return rows
    return [...(asset.trades || [])].sort(sortByCostDescending).map(toScheduleTradeRow)
}

function toAssetScheduleRow(schedule) {
    return {
        id: `asset-schedule:${schedule.scheduleId || schedule.scheduleCode || schedule.scheduleTitle}`,
        label: getScheduleLabel(schedule),
        secondaryLabel: schedule.facilityName || "",
        taskCount: schedule.taskCount || 0,
        actualHours: schedule.actualHours ?? schedule.totalHours ?? 0,
        totalHours: schedule.totalHours || 0,
        totalCost: schedule.totalCost || 0,
        allocatedFixedCost: schedule.allocatedFixedCost || 0,
    }
}

function toAssetScheduleTradeRow(schedule, trade) {
    return {
        id: `asset-schedule-trade:${schedule.scheduleId || schedule.scheduleCode}:${trade.tradeId || trade.tradeName}`,
        label: getScheduleLabel(schedule),
        secondaryLabel: trade.tradeName || "",
        taskCount: trade.taskCount || 0,
        actualHours: trade.actualHours ?? trade.totalHours ?? 0,
        totalHours: trade.totalHours || 0,
        hourlyRate: trade.hourlyRate,
        totalCost: trade.totalCost || 0,
        allocatedFixedCost: trade.allocatedFixedCost || 0,
        mode: trade.mode || "hourly",
    }
}

function getAllocatedScheduleBreakdown(costResult) {
    const fixedContractsByTradeId = getFixedContractsByTradeId(costResult)
    return getScheduleBreakdown(costResult).map((schedule) =>
        applyFixedContractAllocationToSchedule(schedule, fixedContractsByTradeId)
    )
}

function getAllocatedAssetBreakdown(costResult) {
    const fixedContractsByTradeId = getFixedContractsByTradeId(costResult)
    return getAssetBreakdown(costResult).map((asset) =>
        applyFixedContractAllocationToAsset(asset, fixedContractsByTradeId)
    )
}

function getFixedContractsByTradeId(costResult) {
    const fixedContractsByTradeId = new Map()
    for (const contract of costResult?.fixedContracts || []) {
        if (contract.tradeId) fixedContractsByTradeId.set(contract.tradeId, contract)
    }
    return fixedContractsByTradeId
}

function applyFixedContractAllocationToSchedule(schedule, fixedContractsByTradeId) {
    const trades = []
    for (const trade of schedule.trades || []) {
        trades.push(applyFixedContractAllocationToTrade(trade, fixedContractsByTradeId))
    }
    const allocatedFixedCost = sumAllocatedFixedCost(trades)

    return {
        ...schedule,
        allocatedFixedCost,
        totalCost: (schedule.totalCost || 0) + allocatedFixedCost,
        trades,
    }
}

function applyFixedContractAllocationToAsset(asset, fixedContractsByTradeId) {
    const trades = []
    for (const trade of asset.trades || []) {
        trades.push(applyFixedContractAllocationToTrade(trade, fixedContractsByTradeId))
    }
    const schedules = []
    for (const schedule of asset.schedules || []) {
        schedules.push(applyFixedContractAllocationToSchedule(schedule, fixedContractsByTradeId))
    }
    const allocatedFixedCost = getAssetAllocatedFixedCost({ trades, schedules })

    return {
        ...asset,
        allocatedFixedCost,
        totalCost: (asset.totalCost || 0) + allocatedFixedCost,
        trades,
        schedules,
    }
}

function applyFixedContractAllocationToTrade(trade, fixedContractsByTradeId) {
    const allocatedFixedCost = getAllocatedFixedContractCost(trade, fixedContractsByTradeId)
    return {
        ...trade,
        allocatedFixedCost,
        totalCost: (trade.totalCost || 0) + allocatedFixedCost,
    }
}

function getAssetAllocatedFixedCost({ trades, schedules }) {
    const tradeAllocation = sumAllocatedFixedCost(trades)
    if (tradeAllocation) return tradeAllocation
    return sumAllocatedFixedCost(schedules)
}

function getAllocatedFixedContractCost(trade, fixedContractsByTradeId) {
    if (trade?.mode !== MODE_FIXED_ANNUAL) return 0

    const contract = fixedContractsByTradeId.get(trade.tradeId)
    const contractTaskCount = contract?.taskCount || 0
    const tradeTaskCount = trade.taskCount || 0
    if (!contractTaskCount || !tradeTaskCount) return 0

    return ((contract.amount || 0) * tradeTaskCount) / contractTaskCount
}

function sumAllocatedFixedCost(items) {
    return (items || []).reduce((sum, item) => sum + (item.allocatedFixedCost || 0), 0)
}

// Deliberately NO task-based reconstruction fallback: rebuilding these views from
// taskBreakdown silently produced incomplete data (the array was capped at 2,000)
// that also omitted fixed-annual contributions. Database-backed results have no
// inline arrays at all — they are served in pages via buildServer*Rows instead.
function getScheduleBreakdown(costResult) {
    if (!costResult?.scheduleBreakdown?.length) return []
    return [...costResult.scheduleBreakdown].sort(sortScheduleBreakdown)
}

function getAssetBreakdown(costResult) {
    if (!costResult?.assetBreakdown?.length) return []
    return [...costResult.assetBreakdown].sort(sortByCostDescending)
}

function toTradeExportRow(trade, { hasAdjustment, currencySymbol }) {
    const row = { "Trade Name": trade.label }
    if (hasAdjustment) {
        row["Actual Hours"] = round2(trade.actualHours)
        row["Billed Hours"] = round2(trade.totalHours)
    } else {
        row["Total Hours"] = round2(trade.totalHours)
    }
    row["Hourly Rate"] = getHourlyRate(trade, currencySymbol)
    row["Total Cost"] = `${currencySymbol}${formatMoney(trade.totalCost)}`
    row["%"] = `${round2(trade.percentage)}%`
    return row
}

function buildScheduleExportRow({ schedule, trade, currencySymbol }) {
    return {
        "Schedule Code": schedule.scheduleCode || "",
        "Schedule Title": schedule.scheduleTitle || "",
        Facility: schedule.facilityName || "",
        "Trade Name": trade?.tradeName || "No costed trades",
        "Task Count": trade?.taskCount ?? schedule.taskCount ?? 0,
        "Actual Hours": round2(trade?.actualHours ?? schedule.actualHours ?? 0),
        "Billed Hours": round2(trade?.totalHours ?? schedule.totalHours ?? 0),
        "Rate Type": getRateType(trade),
        "Hourly Rate": getHourlyRate(trade, currencySymbol),
        "Cost Basis": getCostBasis(trade || schedule),
        "Total Cost": `${currencySymbol}${formatMoney(trade?.totalCost ?? schedule.totalCost ?? 0)}`,
    }
}

function buildScheduleTotalExportRow({ schedule, currencySymbol }) {
    return {
        "Schedule Code": schedule.scheduleCode || "",
        "Schedule Title": schedule.scheduleTitle || "",
        Facility: schedule.facilityName || "",
        "Trade Name": "Schedule Total",
        "Task Count": schedule.costedTaskCount || schedule.taskCount || 0,
        "Actual Hours": round2(schedule.actualHours ?? 0),
        "Billed Hours": round2(schedule.totalHours ?? 0),
        "Rate Type": "",
        "Hourly Rate": "",
        "Cost Basis": getCostBasis(schedule),
        "Total Cost": `${currencySymbol}${formatMoney(schedule.totalCost ?? 0)}`,
    }
}

function buildAssetExportRow({ asset, detail, currencySymbol }) {
    return {
        "Asset Name": getAssetPrimaryLabel(asset),
        "Asset Tag": asset.assetTag || "",
        "Asset Label": asset.assetLabel || "",
        "Asset Type": asset.assetType || "",
        Category: asset.assetCategory || "",
        Facility: asset.facilityName || "",
        Floor: asset.assetFloor || "",
        Condition: asset.assetCondition || "",
        "SFG20 Code": asset.assetSfg20Code || "",
        "Schedule / Detail": detail?.label || "",
        Trade: detail?.secondaryLabel || "",
        "Task Count": detail?.taskCount ?? asset.taskCount ?? 0,
        "Actual Hours": round2(detail?.actualHours ?? asset.actualHours ?? 0),
        "Billed Hours": round2(detail?.totalHours ?? asset.totalHours ?? 0),
        "Cost Basis": getCostBasis(detail || asset),
        "Total Cost": `${currencySymbol}${formatMoney(detail?.totalCost ?? asset.totalCost ?? 0)}`,
    }
}

function buildAssetTotalExportRow({ asset, currencySymbol }) {
    return {
        "Asset Name": getAssetPrimaryLabel(asset),
        "Asset Tag": asset.assetTag || "",
        "Asset Label": asset.assetLabel || "",
        "Asset Type": asset.assetType || "",
        Category: asset.assetCategory || "",
        Facility: asset.facilityName || "",
        Floor: asset.assetFloor || "",
        Condition: asset.assetCondition || "",
        "SFG20 Code": asset.assetSfg20Code || "",
        "Schedule / Detail": "Asset Total",
        Trade: "",
        "Task Count": asset.taskCount || 0,
        "Actual Hours": round2(asset.actualHours ?? 0),
        "Billed Hours": round2(asset.totalHours ?? 0),
        "Cost Basis": getCostBasis(asset),
        "Total Cost": `${currencySymbol}${formatMoney(asset.totalCost ?? 0)}`,
    }
}

function getRateType(trade) {
    if (!trade) return ""
    if (trade.mode === MODE_FIXED_ANNUAL) return "Fixed annual"
    return "Hourly"
}

function getHourlyRate(trade, currencySymbol) {
    if (!trade || trade.mode === MODE_FIXED_ANNUAL) return ""
    if (trade.hourlyRate == null) return ""
    return `${currencySymbol}${formatMoney(trade.hourlyRate)}`
}

function getCostBasis(item) {
    if (item?.allocatedFixedCost > 0) return COST_BASIS_ALLOCATED_FIXED_ANNUAL
    return ""
}

function getScheduleLabel(schedule) {
    const code = schedule.scheduleCode || ""
    const title = schedule.scheduleTitle || ""
    if (code && title) return `${code} - ${title}`
    return title || code || schedule.scheduleId || "Unknown schedule"
}

function scheduleRowMatchesSearch(row, terms) {
    const text = getScheduleSearchText(row)
    return terms.every((term) => text.includes(term))
}

function assetRowMatchesSearch(row, terms) {
    const text = getAssetSearchText(row)
    return terms.every((term) => text.includes(term))
}

function isSearchableBreakdownView(view) {
    return view === BREAKDOWN_VIEW_SCHEDULE || view === BREAKDOWN_VIEW_ASSET
}

function getScheduleSearchText(row) {
    const source = row.detailSource || {}
    return [
        row.label,
        row.secondaryLabel,
        source.scheduleCode,
        source.scheduleTitle,
        source.facilityName,
        source.facilityId,
    ]
        .map(normaliseSearchText)
        .join(" ")
}

function getAssetSearchText(row) {
    const source = row.detailSource || {}
    return [
        row.label,
        row.secondaryLabel,
        source.assetType,
        getAssetTypeDisplayName(source.assetType),
        source.facilityName,
        source.facilityId,
        source.assetFloor,
    ]
        .map(normaliseSearchText)
        .join(" ")
}

function getSearchTerms(search) {
    return normaliseSearchText(search).split(/\s+/).filter(Boolean)
}

function normaliseSearchText(value) {
    return `${value || ""}`.trim().toLowerCase()
}

function getAssetPrimaryLabel(asset) {
    return (
        firstMeaningfulText([
            asset.assetAiCombined,
            asset.assetAiType,
            asset.assetAiParent,
            asset.assetDescription,
            getAssetTypeDisplayName(asset.assetType),
            asset.assetCategory,
            asset.assetLabel,
            asset.assetTag,
            asset.assetId,
        ]) || "Unknown asset"
    )
}

function getAssetSecondaryLabel(asset, primaryLabel) {
    return [getAssetTagLabel(asset, primaryLabel), asset.facilityName, asset.assetFloor, getAssetConditionLabel(asset)]
        .filter(Boolean)
        .join(" / ")
}

function getAssetTagLabel(asset, primaryLabel) {
    const tag = firstMeaningfulText([asset.assetTag, asset.assetLabel])
    if (!tag || tag === primaryLabel) return ""
    return `Asset ${tag}`
}

function getAssetConditionLabel(asset) {
    if (!asset.assetCondition) return ""
    return `Condition ${asset.assetCondition}`
}

function getAssetTypeDisplayName(assetType) {
    const parts = getAssetTypeParts(assetType)
    if (!parts.length) return ""
    return parts[parts.length - 1]
}

function getAssetTypeParts(assetType) {
    return `${assetType || ""}`.split(",").map(cleanAssetText).filter(Boolean).filter(isMeaningfulAssetText)
}

function firstMeaningfulText(values) {
    for (const value of values) {
        const text = cleanAssetText(value)
        if (isMeaningfulAssetText(text)) return text
    }
    return ""
}

function cleanAssetText(value) {
    return `${value || ""}`.trim()
}

function isMeaningfulAssetText(value) {
    return value !== "" && value !== "Unknown"
}

function getSkillSecondaryLabel(skill) {
    if (!skill.isOverride) return ""
    return `Override ${skill.hourlyRate}/hr`
}

function hasRowBillingAdjustment(row) {
    if (!("actualHours" in row)) return false
    return row.actualHours != null && row.actualHours !== row.totalHours
}

function sortScheduleBreakdown(a, b) {
    const codeComparison = `${a.scheduleCode || ""}`.localeCompare(`${b.scheduleCode || ""}`, undefined, {
        numeric: true,
    })
    if (codeComparison) return codeComparison
    const titleComparison = `${a.scheduleTitle || ""}`.localeCompare(`${b.scheduleTitle || ""}`)
    if (titleComparison) return titleComparison
    return `${a.facilityName || ""}`.localeCompare(`${b.facilityName || ""}`)
}

function sortByCostDescending(a, b) {
    const costComparison = (b.totalCost || 0) - (a.totalCost || 0)
    if (costComparison) return costComparison
    return `${a.tradeName || a.assetLabel || a.label || ""}`.localeCompare(
        `${b.tradeName || b.assetLabel || b.label || ""}`,
        undefined,
        { numeric: true }
    )
}
